import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Presentation } from 'lucide-react';
import { presentationHubAPI } from '@/shared/services/api';
import { OriginalPresentationDeck } from './OriginalPresentationDecks';
import { getPresentationRouteByPath, PRESENTATION_HUB_PUBLIC_ROUTES } from './presentationHubRoutes';

export default function PublicPresentationPage() {
  const location = useLocation();
  const staticRoute = getPresentationRouteByPath(location.pathname);
  const [dynamicRoute, setDynamicRoute] = useState(null);
  const [loadingDynamicRoute, setLoadingDynamicRoute] = useState(!staticRoute);
  const route = staticRoute || dynamicRoute;

  useLayoutEffect(() => {
    document.body.classList.add('presentation-hub-public-route');
    return () => {
      document.body.classList.remove('presentation-hub-public-route');
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (staticRoute) {
      setLoadingDynamicRoute(false);
      setDynamicRoute(null);
      return undefined;
    }

    setLoadingDynamicRoute(true);
    presentationHubAPI.getPublicPresentationByPath(location.pathname)
      .then((data) => {
        if (!cancelled) setDynamicRoute(data);
      })
      .catch(() => {
        if (!cancelled) setDynamicRoute(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingDynamicRoute(false);
      });

    return () => {
      cancelled = true;
    };
  }, [location.pathname, staticRoute]);

  if (loadingDynamicRoute) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-950 p-6 text-white">
        <div className="max-w-lg rounded-xl border border-white/10 bg-white/[0.04] p-6 text-center">
          <h1 className="text-2xl font-semibold">Loading presentation...</h1>
          <p className="mt-2 text-slate-300">Preparing the public presentation route.</p>
        </div>
      </main>
    );
  }

  if (!route) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-950 p-6 text-white">
        <div className="max-w-lg rounded-xl border border-white/10 bg-white/[0.04] p-6 text-center">
          <h1 className="text-2xl font-semibold">Presentation route not found</h1>
          <p className="mt-2 text-slate-300">
            Available routes: {PRESENTATION_HUB_PUBLIC_ROUTES.map((item) => item.publicPath).join(', ')}
          </p>
        </div>
      </main>
    );
  }

  if (route.renderer === 'html') {
    return (
      <main className="min-h-screen bg-slate-950">
        <iframe
          title={route.name}
          srcDoc={route.html || '<!doctype html><html><body><h1>Presentation content unavailable</h1></body></html>'}
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
          className="h-screen w-full border-0 bg-white"
        />
      </main>
    );
  }

  return <OriginalPresentationDeck sourceDeck={route.sourceDeck} />;
}
